import type { MotionAmplificationJob } from "../../clients/motion-amplification";

export type MotionAmplificationDateRange =
  | "all"
  | "today"
  | "last7Days"
  | "last30Days"
  | "last90Days"
  | "custom";

export type MotionAmplificationFilters = {
  searchTerm: string;
  customerIds: string[];
  facilityIds: string[];
  machineIds: string[];
  statuses: string[];
  frameRates: number[];
  dateRange: MotionAmplificationDateRange;
  dateStart: Date | null;
  dateEnd: Date | null;
};

export const defaultMotionAmplificationFilters: MotionAmplificationFilters = {
  searchTerm: "",
  customerIds: [],
  facilityIds: [],
  machineIds: [],
  statuses: [],
  frameRates: [],
  dateRange: "all",
  dateStart: null,
  dateEnd: null,
};

export type MotionAmplificationFilterOption = {
  value: string;
  label: string;
};

export type MotionAmplificationFilterOptions = {
  customers: MotionAmplificationFilterOption[];
  facilities: MotionAmplificationFilterOption[];
  machines: MotionAmplificationFilterOption[];
  frameRates: number[];
};

function uniqueOptions(options: MotionAmplificationFilterOption[]) {
  return Array.from(
    new Map(options.map((option) => [option.value, option])).values(),
  ).sort((a, b) => a.label.localeCompare(b.label));
}

export function roundedFrameRate(job: MotionAmplificationJob) {
  const fps = job.parameters?.fps;
  return typeof fps === "number" && Number.isFinite(fps)
    ? Math.round(fps)
    : null;
}

export function getMotionAmplificationFilterOptions(
  jobs: MotionAmplificationJob[],
): MotionAmplificationFilterOptions {
  const customers: MotionAmplificationFilterOption[] = [];
  const facilities: MotionAmplificationFilterOption[] = [];
  const machines: MotionAmplificationFilterOption[] = [];
  const frameRates = new Set<number>();

  for (const job of jobs) {
    const frameRate = roundedFrameRate(job);
    if (frameRate !== null) frameRates.add(frameRate);
    if (!job.assignment) continue;

    customers.push({
      value: job.assignment.customerId,
      label: job.assignment.customerName,
    });
    facilities.push({
      value: job.assignment.facilityId,
      label: job.assignment.facilityName,
    });
    machines.push({
      value: job.assignment.machineId,
      label: `${job.assignment.lineName ?? "—"} | ${job.assignment.machineName}`,
    });
  }

  return {
    customers: uniqueOptions(customers),
    facilities: uniqueOptions(facilities),
    machines: uniqueOptions(machines),
    frameRates: [...frameRates].sort((a, b) => a - b),
  };
}

function matchesAny(
  selectedValues: string[],
  value: string | null | undefined,
) {
  return selectedValues.length === 0 || selectedValues.includes(value ?? "");
}

function matchesFrameRate(
  selectedFrameRates: number[],
  job: MotionAmplificationJob,
) {
  return (
    selectedFrameRates.length === 0 ||
    selectedFrameRates.includes(roundedFrameRate(job) ?? Number.NaN)
  );
}

function startOfDay(date: Date) {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start;
}

function endOfDay(date: Date) {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end;
}

function matchesDateRange(
  dateCreated: string | null | undefined,
  filters: MotionAmplificationFilters,
  now: Date,
) {
  if (filters.dateRange === "all") return true;
  if (!dateCreated) return false;

  const createdAt = new Date(dateCreated);
  if (Number.isNaN(createdAt.getTime())) return false;

  if (filters.dateRange === "custom") {
    const start = filters.dateStart ? startOfDay(filters.dateStart) : null;
    const end = filters.dateEnd ? endOfDay(filters.dateEnd) : null;
    return (
      (start === null || createdAt >= start) &&
      (end === null || createdAt <= end)
    );
  }

  const start = new Date(now);
  if (filters.dateRange === "today") {
    start.setHours(0, 0, 0, 0);
  } else {
    const days =
      filters.dateRange === "last7Days"
        ? 7
        : filters.dateRange === "last30Days"
          ? 30
          : 90;
    start.setDate(start.getDate() - days);
  }

  return createdAt >= start && createdAt <= now;
}

function matchesSearch(job: MotionAmplificationJob, searchTerm: string) {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  if (!normalizedSearchTerm) return true;

  const searchableValues = [job.fileName];
  if (job.assignment) {
    searchableValues.push(
      job.assignment.machineName,
      job.assignment.facilityName,
      job.assignment.customerName,
    );
  }

  return searchableValues.some(
    (value) => value?.toLowerCase().includes(normalizedSearchTerm) ?? false,
  );
}

export function matchesMotionAmplificationFilters(
  job: MotionAmplificationJob,
  filters: MotionAmplificationFilters,
  now = new Date(),
) {
  return (
    matchesSearch(job, filters.searchTerm) &&
    matchesAny(filters.customerIds, job.assignment?.customerId) &&
    matchesAny(filters.facilityIds, job.assignment?.facilityId) &&
    matchesAny(filters.machineIds, job.assignment?.machineId) &&
    matchesAny(filters.statuses, job.status) &&
    matchesFrameRate(filters.frameRates, job) &&
    matchesDateRange(job.dateCreated, filters, now)
  );
}

export function hasActiveMotionAmplificationFilters(
  filters: MotionAmplificationFilters,
) {
  return (
    filters.searchTerm.trim().length > 0 ||
    filters.customerIds.length > 0 ||
    filters.facilityIds.length > 0 ||
    filters.machineIds.length > 0 ||
    filters.statuses.length > 0 ||
    filters.frameRates.length > 0 ||
    filters.dateRange !== "all" ||
    filters.dateStart !== null ||
    filters.dateEnd !== null
  );
}
